import { useState } from "react";
import { api, waLink, WHATSAPP_DISPLAY, CONTACT_EMAIL } from "@/lib/config";
import { toast } from "sonner";
import { Mail, MessageCircle, MapPin } from "lucide-react";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll be in touch within 24 hours.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Couldn't send right now. Please try WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-36 md:pt-40 pb-24 bg-[#FAF6F0]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-3xl mx-auto">
          <p className="overline">get in touch</p>
          <h1 className="font-display text-5xl md:text-7xl italic font-light mt-4 leading-tight">Talk to a Real Human</h1>
          <p className="mt-5 text-[#23170E]/65">
            We answer every message ourselves — usually within an hour during Tanzanian daylight,
            and within 24 hours otherwise. WhatsApp is fastest.
          </p>
        </div>

        <div className="mt-16 grid md:grid-cols-12 gap-8">
          <form onSubmit={submit} className="md:col-span-7 space-y-6 bg-[#FFFFFF] border border-[#E2D5C3] rounded-3xl shadow-[0_4px_20px_-2px_rgba(35,23,14,0.06)] p-8 md:p-10">
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="Name" required>
                <input data-testid="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="cinput" />
              </Field>
              <Field label="Email" required>
                <input data-testid="contact-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="cinput" />
              </Field>
            </div>
            <Field label="Subject">
              <input data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="cinput" />
            </Field>
            <Field label="Message" required>
              <textarea data-testid="contact-message" required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="cinput resize-none" />
            </Field>
            <button data-testid="contact-submit" type="submit" disabled={submitting} className="btn-pill btn-pill-primary">
              {submitting ? "Sending…" : "Submit"}
            </button>
          </form>

          <aside className="md:col-span-5 space-y-5">
            <a href={waLink("Hi APEX, I'd like to plan a Tanzania safari.")} data-testid="contact-whatsapp-card" target="_blank" rel="noreferrer" className="block rounded-3xl p-8 bg-[#25D366] text-white hover:bg-[#1ebd5b] transition-colors">
              <MessageCircle size={32} className="mb-4" />
              <p className="text-xs uppercase tracking-[0.22em] text-white/85">Fastest</p>
              <p className="font-marcellus text-2xl mt-1">WhatsApp us</p>
              <p className="mt-2 text-white/90">{WHATSAPP_DISPLAY}</p>
            </a>
            <div className="rounded-3xl p-8 bg-[#FFFFFF] border border-[#E2D5C3]">
              <Mail size={28} className="text-[#A6521B] mb-4" />
              <p className="overline">Email</p>
              <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-email-link" className="mt-2 block hover:text-[#A6521B] break-all">{CONTACT_EMAIL}</a>
            </div>
            <div className="rounded-3xl p-8 bg-[#FFFFFF] border border-[#E2D5C3]">
              <MapPin size={28} className="text-[#A6521B] mb-4" />
              <p className="overline">Office</p>
              <p className="mt-2">Sokoine Road, Arusha, Tanzania</p>
              <p className="text-sm text-[#23170E]/55 mt-1">Mon-Sat · 8am-6pm EAT</p>
            </div>
          </aside>
        </div>
      </div>

      <style>{`
        .cinput { width: 100%; background: #FAF6F0; border: 1px solid rgba(58, 39, 24, 0.18); border-radius: 9999px; padding: 12px 20px; color: #23170E; outline: none; transition: border-color 200ms; font-family: 'Manrope', sans-serif; font-size: 0.95rem; }
        .cinput:focus { border-color: #A6521B; }
        textarea.cinput { border-radius: 24px; padding: 14px 20px; }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="overline">{label}{required && " *"}</span>
      <div className="mt-3">{children}</div>
    </label>
  );
}
